import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardBody, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Mail, Save, RefreshCw, Users, CheckCircle, Clock, Zap } from 'lucide-react';
import { api } from '../../utils/api';
import toast from 'react-hot-toast';

function Toggle({ value, onChange }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!value)}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${value ? 'bg-primary' : 'bg-gray-300'}`}
    >
      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${value ? 'translate-x-6' : 'translate-x-1'}`} />
    </button>
  );
}

export function NewsletterMarketingPage() {
  // Newsletter Config
  const [config, setConfig] = useState({
    discount_type: 'fixed_amount',
    discount_value: '10',
    discount_prefix: 'WELCOME',
    expires_days: '30',
    is_active: true,
  });
  const [stats, setStats] = useState({ pending: 0, confirmed: 0, unsubscribed: 0, total: 0 });
  const [saving, setSaving] = useState(false);

  // Instant Discount Config
  const [instantConfig, setInstantConfig] = useState({
    discount_type: 'fixed_amount',
    discount_value: '20',
    discount_prefix: 'INSTANT',
    expires_hours: '24',
    visit_window_minutes: '60',
    cooldown_hours: '48',
    is_active: true,
  });
  const [savingInstant, setSavingInstant] = useState(false);

  const [loading, setLoading] = useState(true);

  useEffect(() => { loadData(); }, []);

  async function loadData() {
    setLoading(true);
    try {
      const [configRes, statsRes, instantRes] = await Promise.all([
        api.getNewsletterConfig(),
        api.getNewsletterStats(),
        api.getInstantDiscountConfig(),
      ]);

      if (configRes.success && configRes.config) {
        setConfig({
          discount_type: configRes.config.discount_type,
          discount_value: String(configRes.config.discount_value),
          discount_prefix: configRes.config.discount_prefix,
          expires_days: String(configRes.config.expires_days),
          is_active: configRes.config.is_active,
        });
      }
      if (statsRes.success) setStats(statsRes.stats);
      if (instantRes.success && instantRes.config) {
        setInstantConfig({
          discount_type: instantRes.config.discount_type,
          discount_value: String(instantRes.config.discount_value),
          discount_prefix: instantRes.config.discount_prefix,
          expires_hours: String(instantRes.config.expires_hours),
          visit_window_minutes: String(instantRes.config.visit_window_minutes),
          cooldown_hours: String(instantRes.config.cooldown_hours),
          is_active: instantRes.config.is_active,
        });
      }
    } catch (err) {
      toast.error('Fehler beim Laden der Konfiguration');
    } finally {
      setLoading(false);
    }
  }

  async function handleSaveNewsletter(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await api.saveNewsletterConfig({
        discount_type: config.discount_type,
        discount_value: parseFloat(config.discount_value),
        discount_prefix: config.discount_prefix,
        expires_days: parseInt(config.expires_days, 10),
        is_active: config.is_active,
      });
      if (res.success) toast.success('Newsletter-Konfiguration gespeichert');
      else toast.error(res.error || 'Speichern fehlgeschlagen');
    } catch (err) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  }

  async function handleSaveInstant(e) {
    e.preventDefault();
    setSavingInstant(true);
    try {
      const res = await api.saveInstantDiscountConfig({
        discount_type: instantConfig.discount_type,
        discount_value: parseFloat(instantConfig.discount_value),
        discount_prefix: instantConfig.discount_prefix,
        expires_hours: parseInt(instantConfig.expires_hours, 10),
        visit_window_minutes: parseInt(instantConfig.visit_window_minutes, 10),
        cooldown_hours: parseInt(instantConfig.cooldown_hours, 10),
        is_active: instantConfig.is_active,
      });
      if (res.success) toast.success('Sofortrabatt-Konfiguration gespeichert');
      else toast.error(res.error || 'Speichern fehlgeschlagen');
    } catch (err) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSavingInstant(false);
    }
  }

  const nlLabel = config.discount_type === 'percentage'
    ? `${config.discount_value || 0}%` : `€${parseFloat(config.discount_value || 0).toFixed(2)}`;
  const nlExampleCode = `${(config.discount_prefix || 'WELCOME').toUpperCase()}-A3K9`;

  const instLabel = instantConfig.discount_type === 'percentage'
    ? `${instantConfig.discount_value || 0}%` : `€${parseFloat(instantConfig.discount_value || 0).toFixed(2)}`;
  const instExampleCode = `${(instantConfig.discount_prefix || 'INSTANT').toUpperCase()}-X7M2`;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Mail className="w-7 h-7 text-primary" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Newsletter Marketing</h1>
            <p className="text-sm text-gray-500">Lead-Capture & automatische Shopify-Rabattcodes</p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={loadData}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Aktualisieren
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardBody className="flex items-center gap-4 py-4">
            <div className="p-2 bg-yellow-50 rounded-lg"><Clock className="w-6 h-6 text-yellow-500" /></div>
            <div>
              <p className="text-sm text-gray-500">Ausstehend (DOI)</p>
              <p className="text-2xl font-bold text-gray-900">{stats.pending}</p>
            </div>
          </CardBody>
        </Card>
        <Card>
          <CardBody className="flex items-center gap-4 py-4">
            <div className="p-2 bg-green-50 rounded-lg"><CheckCircle className="w-6 h-6 text-green-500" /></div>
            <div>
              <p className="text-sm text-gray-500">Bestätigt</p>
              <p className="text-2xl font-bold text-gray-900">{stats.confirmed}</p>
            </div>
          </CardBody>
        </Card>
        <Card>
          <CardBody className="flex items-center gap-4 py-4">
            <div className="p-2 bg-blue-50 rounded-lg"><Users className="w-6 h-6 text-blue-500" /></div>
            <div>
              <p className="text-sm text-gray-500">Gesamt Leads</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
          </CardBody>
        </Card>
      </div>

      {/* Sofortrabatt Config */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-amber-500" />
            <CardTitle>Sofortrabatt — "Jetzt zur Kasse"</CardTitle>
          </div>
        </CardHeader>
        <CardBody>
          <form onSubmit={handleSaveInstant} className="space-y-6">
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <p className="font-medium text-gray-900">Sofortrabatt aktiv</p>
                <p className="text-sm text-gray-500">
                  Neue Besucher sehen ein Badge am "Jetzt zur Kasse"-Button und bekommen beim Checkout automatisch einen Code.
                </p>
              </div>
              <Toggle value={instantConfig.is_active} onChange={v => setInstantConfig(c => ({ ...c, is_active: v }))} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Rabatt-Typ</label>
                <select
                  value={instantConfig.discount_type}
                  onChange={e => setInstantConfig(c => ({ ...c, discount_type: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="fixed_amount">Fixer Betrag (€)</option>
                  <option value="percentage">Prozentsatz (%)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Rabatt-Wert {instantConfig.discount_type === 'percentage' ? '(%)' : '(€)'}
                </label>
                <input type="number" min="0.01" step={instantConfig.discount_type === 'percentage' ? '1' : '0.01'}
                  value={instantConfig.discount_value}
                  onChange={e => setInstantConfig(c => ({ ...c, discount_value: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Code-Präfix</label>
                <input type="text" value={instantConfig.discount_prefix}
                  onChange={e => setInstantConfig(c => ({ ...c, discount_prefix: e.target.value.toUpperCase() }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono"
                  maxLength={20} required />
                <p className="mt-1 text-xs text-gray-400">Beispiel: <span className="font-mono font-medium">{instExampleCode}</span></p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Code gültig (Stunden)</label>
                <input type="number" min="1" value={instantConfig.expires_hours}
                  onChange={e => setInstantConfig(c => ({ ...c, expires_hours: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Zeitfenster (Min.) — Besucher muss binnen X Minuten buchen
                </label>
                <input type="number" min="1" value={instantConfig.visit_window_minutes}
                  onChange={e => setInstantConfig(c => ({ ...c, visit_window_minutes: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Cooldown (Stunden) — kein erneuter Rabatt für X Stunden
                </label>
                <input type="number" min="1" value={instantConfig.cooldown_hours}
                  onChange={e => setInstantConfig(c => ({ ...c, cooldown_hours: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary" required />
              </div>
            </div>

            <div className="p-4 bg-amber-50 border border-amber-100 rounded-lg text-sm text-amber-800">
              <p className="font-medium mb-1">Vorschau: Was der Kunde sieht</p>
              <p>Badge: <strong>{instLabel} Sofortrabatt</strong> · Code z.B. <span className="font-mono font-bold">{instExampleCode}</span> · Einmalig · {instantConfig.expires_hours}h gültig</p>
              <p className="mt-1 text-amber-700">Gilt für Besucher, die innerhalb von {instantConfig.visit_window_minutes} Minuten nach dem ersten Seitenaufruf buchen.</p>
            </div>

            <div className="flex justify-end">
              <Button type="submit" disabled={savingInstant}>
                {savingInstant ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Speichern...</> : <><Save className="w-4 h-4 mr-2" />Sofortrabatt speichern</>}
              </Button>
            </div>
          </form>
        </CardBody>
      </Card>

      {/* Newsletter Config */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-primary" />
            <CardTitle>Newsletter — "Für später merken"</CardTitle>
          </div>
        </CardHeader>
        <CardBody>
          <form onSubmit={handleSaveNewsletter} className="space-y-6">
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <p className="font-medium text-gray-900">Newsletter-Kampagne aktiv</p>
                <p className="text-sm text-gray-500">
                  Bei bestätigten Subscribern wird ein Rabattcode generiert und per E-Mail versendet.
                </p>
              </div>
              <Toggle value={config.is_active} onChange={v => setConfig(c => ({ ...c, is_active: v }))} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Rabatt-Typ</label>
                <select value={config.discount_type}
                  onChange={e => setConfig(c => ({ ...c, discount_type: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary">
                  <option value="fixed_amount">Fixer Betrag (€)</option>
                  <option value="percentage">Prozentsatz (%)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Rabatt-Wert {config.discount_type === 'percentage' ? '(%)' : '(€)'}
                </label>
                <input type="number" min="0.01" step={config.discount_type === 'percentage' ? '1' : '0.01'}
                  value={config.discount_value}
                  onChange={e => setConfig(c => ({ ...c, discount_value: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Code-Präfix</label>
                <input type="text" value={config.discount_prefix}
                  onChange={e => setConfig(c => ({ ...c, discount_prefix: e.target.value.toUpperCase() }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono"
                  maxLength={20} required />
                <p className="mt-1 text-xs text-gray-400">Beispiel: <span className="font-mono font-medium">{nlExampleCode}</span></p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Gültigkeit (Tage ab Generierung)</label>
                <input type="number" min="1" value={config.expires_days}
                  onChange={e => setConfig(c => ({ ...c, expires_days: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary" required />
              </div>
            </div>

            <div className="p-4 bg-blue-50 border border-blue-100 rounded-lg text-sm text-blue-800">
              <p className="font-medium mb-1">Vorschau: Was der Subscriber bekommt</p>
              <p>Code <span className="font-mono font-bold">{nlExampleCode}</span> · {nlLabel} Rabatt · Einmalig · {config.expires_days || 30} Tage gültig</p>
            </div>

            <div className="p-4 bg-yellow-50 border border-yellow-100 rounded-lg text-sm text-yellow-800">
              <p className="font-medium mb-1">Brevo-Konfiguration erforderlich</p>
              <ul className="list-disc list-inside space-y-1 text-yellow-700">
                <li>API-Key: <span className="font-mono">BREVO_API_KEY</span></li>
                <li>Listen-ID: <span className="font-mono">BREVO_LIST_ID</span></li>
                <li>Webhook: <span className="font-mono">contact.subscribed</span> → <span className="font-mono">/api/newsletter/webhook/brevo</span></li>
                <li>Webhook-Secret: <span className="font-mono">BREVO_WEBHOOK_SECRET</span></li>
              </ul>
            </div>

            <div className="flex justify-end">
              <Button type="submit" disabled={saving}>
                {saving ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Speichern...</> : <><Save className="w-4 h-4 mr-2" />Newsletter speichern</>}
              </Button>
            </div>
          </form>
        </CardBody>
      </Card>
    </div>
  );
}
